import { createContext, useContext, useEffect, useState } from "react";
import { posthog, posthogLoaded } from "@/lib/posthog";

/**
 * Ciudades con salas asociadas (adaptado del CityContext de RE:PÚBLICO).
 * Lista estática a propósito: el modal de primera visita debe pintarse al
 * instante, sin esperar a la tabla de cines. Los valores coinciden con
 * cinemas.city tras normalizeCity (Gandía se pliega en Valencia). Cuando se
 * sume una ciudad nueva, añádela aquí.
 */
export const SUPPORTED_CITIES = ["Barcelona", "Valencia"] as const;
export type City = (typeof SUPPORTED_CITIES)[number];

const STORAGE_KEY = "recine_city";

const isSupported = (v: string | null): v is City =>
  !!v && (SUPPORTED_CITIES as readonly string[]).includes(v);

interface CityContextValue {
  /** Ciudad elegida; null = primera visita, aún sin elegir (abre el chooser). */
  city: City | null;
  setCity: (c: City, source?: string) => void;
}

const Ctx = createContext<CityContextValue>({ city: null, setCity: () => {} });

export const useCity = () => useContext(Ctx);

export function CityProvider({ children }: { children: React.ReactNode }) {
  const [city, setCityState] = useState<City | null>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return isSupported(stored) ? stored : null;
    } catch {
      return null;
    }
  });

  const setCity = (c: City, source?: string) => {
    setCityState(c);
    try {
      localStorage.setItem(STORAGE_KEY, c);
    } catch {
      /* sin persistencia: la elección vive en memoria */
    }
    if (posthogLoaded()) {
      posthog().capture("city_selected", { city: c, source: source ?? "unknown" });
    }
  };

  // Dimensión ciudad en la analítica: todos los eventos llevan la ciudad activa.
  useEffect(() => {
    if (city && posthogLoaded()) posthog().register({ city });
  }, [city]);

  return <Ctx.Provider value={{ city, setCity }}>{children}</Ctx.Provider>;
}
